import {
  createMuiTheme,
  ThemeOptions,
} from '@material-ui/core';
import { DefaultTheme } from 'styled-components';

const palette = {
  primary50: '#eaf4fa',
  primary100: '#cfe6f3',
  primary200: '#a1cde6',
  primary300: '#6fb0d5',
  primary400: '#3d8fbe',
  primary500: '#1476a8',
  primary600: '#0b6b9b',
  primary700: '#085780',
  primary800: '#064464',
  primary900: '#042f46',

  neutral0: '#ffffff',
  neutral50: '#f8f9fb',
  neutral100: '#f2f4f7',
  neutral200: '#e6e9ef',
  neutral300: '#d3d7e0',
  neutral400: '#a9afbd',
  neutral500: '#79808f',
  neutral600: '#555c6a',
  neutral700: '#3b424e',
  neutral800: '#242932',
  neutral900: '#131720',

  success50: '#e8f6ee',
  success100: '#c5e7d2',
  success500: '#2f9e5e',
  success600: '#22864c',
  success700: '#196a3b',

  warning50: '#fff6e0',
  warning100: '#ffe7ac',
  warning500: '#e0a012',
  warning600: '#b7800a',
  warning700: '#8a5f05',

  danger50: '#fdecec',
  danger100: '#f9c9c9',
  danger500: '#e0423f',
  danger600: '#c3302d',
  danger700: '#9a2523',

  info50: '#e8f2fd',
  info500: '#2b7be0',
  info600: '#1e64bf',
};

const theme: DefaultTheme = {
  linkText: '#111',
  mainText: '#414141',
  secondaryText: '#888',
  primary: palette.primary600,
  secondary: '#f5f5f5',
  border: '#f0f0f0',
  defaultBackground: '#ffffff',
  defaultTableCell: '#fcf9fc',
  defaultShadow: '#000000',
  disabledButton: '#ddd',
  defaultTableCellTitle: '#949494',
  mainRequired: '#FF3D3D',

  marker1: '#a7e0f4',
  marker2: '#dfb9c9',
  marker3: '#d73965',
  marker4: '#48a9ee',
  marker5: '#e25241',
  marker6: '#6b77c4',

  palette,

  space: {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
  },

  radius: {
    sm: '4px',
    md: '6px',
    lg: '10px',
    xl: '16px',
    full: '9999px',
  },

  shadow: {
    xs: '0 1px 2px rgba(19, 23, 32, 0.06)',
    sm: '0 1px 3px rgba(19, 23, 32, 0.08), 0 1px 2px rgba(19, 23, 32, 0.04)',
    md: '0 4px 10px rgba(19, 23, 32, 0.08), 0 2px 4px rgba(19, 23, 32, 0.04)',
    lg: '0 12px 28px rgba(19, 23, 32, 0.12), 0 6px 10px rgba(19, 23, 32, 0.05)',
    focus: `0 0 0 3px ${palette.primary200}`,
    focusDanger: `0 0 0 3px ${palette.danger100}`,
  },

  fontSize: {
    xs: '11px',
    sm: '12px',
    md: '13px',
    base: '14px',
    lg: '15px',
    xl: '17px',
    '2xl': '20px',
    '3xl': '24px',
    '4xl': '30px',
  },

  lineHeight: {
    tight: '1.25',
    snug: '1.35',
    normal: '1.5',
    relaxed: '1.65',
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  motion: {
    fast: '120ms',
    base: '180ms',
    slow: '260ms',
    ease: 'cubic-bezier(0.2, 0, 0, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  },
};

export const fontFamily = [
  '"Inter"',
  '"Segoe UI"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Helvetica Neue"',
  'Arial',
  'Roboto',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

export const muiTheme = createMuiTheme({
  palette: {
    primary: {
      main: theme.primary,
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  shadows: new Array(25).fill(
    'none',
  ) as ThemeOptions['shadows'],
  typography: {
    fontFamily,
  },
});

export default theme;
